import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, PageHeader, Section, EmptyState } from "@/components/Primitives";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [overview, setOverview] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data));
    api.get("/subjects").then(r => setSubjects(r.data));
    api.get("/analytics/students-overview").then(r => setOverview(r.data));
  }, []);

  const byRole = (r) => users.filter(u => u.role === r).length;
  const avgPerf = overview.length ? (overview.reduce((s, x) => s + x.overall_percentage, 0) / overview.length).toFixed(1) : "—";

  return (
    <div>
      <PageHeader eyebrow="Administration" title="Institution overview" subtitle="Platform-wide snapshot of users, content and performance." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
        <StatCard label="Total users" value={users.length} sub={`${byRole("admin")} admin · ${byRole("teacher")} faculty · ${byRole("student")} students`} />
        <StatCard label="Subjects" value={subjects.length} sub="across all streams" />
        <StatCard label="Avg. performance" value={`${avgPerf}%`} sub="All students, all tests" accent="terracotta" />
        <StatCard label="Active streams" value={new Set(subjects.map(s => s.class_group)).size} sub="classes / programmes" />
      </div>

      <Section title="Top performers">
        {overview.length === 0 ? <EmptyState title="No student attempts yet" /> : (
          <div className="card-academic overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-sand text-ink">
                <tr>
                  <Th>Rank</Th><Th>Student</Th><Th>Class</Th><Th>Tests</Th><Th right>Overall</Th>
                </tr>
              </thead>
              <tbody>
                {overview.slice(0, 10).map((o, i) => (
                  <tr key={o.id} className="border-b border-stone hover:bg-bone/50">
                    <Td><span className="font-mono">{String(i + 1).padStart(2, "0")}</span></Td>
                    <Td>{o.name}</Td>
                    <Td className="text-mossy">{o.class_group || "—"}</Td>
                    <Td className="font-mono">{o.tests_taken}</Td>
                    <Td right><span className="font-mono font-semibold text-forest">{o.overall_percentage}%</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Quick actions">
        <div className="grid md:grid-cols-3 gap-4">
          <ActionCard title="Manage users" body="Invite faculty, enroll students, audit activity." onClick={() => nav("/admin/users")} />
          <ActionCard title="Subjects & chapters" body="Configure the curriculum taxonomy." onClick={() => nav("/admin/subjects")} />
          <ActionCard title="Broadcast alert" body="Send an institution-wide announcement." onClick={() => nav("/teacher/notify")} />
        </div>
      </Section>
    </div>
  );
}

function Th({ children, right }) { return <th className={`p-4 overline ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`p-4 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
function ActionCard({ title, body, onClick }) {
  return (
    <button onClick={onClick} className="card-academic p-6 text-left hover:border-forest" data-testid={`action-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-serif text-xl text-forest mb-1">{title}</div>
      <div className="text-sm text-mossy">{body}</div>
    </button>
  );
}
